// Kundenexport — Zeit-/Leistungsnachweis pro Kunde als druckfertiges PDF (via
// Browser-Druck „Als PDF sichern") + optionaler CSV.
//
// Rein lesend aus den bereits geladenen Daten (DataContext). Keine Stundensätze,
// keine Beträge — bewusst kundentauglich. Keine Migration, keine neuen Queries.
//
// Aufbau: pro Kunde ein Dokument, gruppiert nach Projekt, darunter die einzelnen
// Zeitbuchungen (Datum, Beschreibung, Dauer) mit Zwischensumme je Projekt und
// Gesamtsumme. Mitarbeiternamen werden bewusst NICHT ausgewiesen.

import { durationMin, pad } from './format.js';

// --- Formatierung -----------------------------------------------------------

// Datum ISO (YYYY-MM-DD) → DD.MM.YYYY. Leer bei Fehlwert.
export function deDate(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

// Minuten → "H:MM h" (z. B. 90 → "1:30 h"). Kundentaugliche Stundenangabe.
export function hoursColon(min) {
  const sign = min < 0 ? '-' : '';
  const abs = Math.abs(Math.round(min));
  return `${sign}${Math.floor(abs / 60)}:${pad(abs % 60)} h`;
}

// Minuten → Dezimalstunden mit Komma (z. B. 90 → "1,50"). Für Summenzeile/CSV.
export function hoursDecimal(min) {
  return (Math.round((min / 60) * 100) / 100).toFixed(2).replace('.', ',');
}

// --- Report-Aufbau ----------------------------------------------------------

// Baut das strukturierte Report-Modell für EINEN Kunden im Zeitraum.
//   data:  { timeEntries, projects, customers } (aus DataContext)
//   customerId: Kunden-ID
//   range: { from, to } (ISO, inklusiv; from/to optional)
// Rückgabe:
//   { customer, rangeLabel, groups: [{ id, title, entries, totalMin }],
//     entryCount, totalMin }
export function buildCustomerReport(data, customerId, range) {
  const { timeEntries, projects, customers } = data;
  const customer = customers.find(c => c.id === customerId) || null;
  const projectById = {};
  for (const p of projects) projectById[p.id] = p;

  // Alle Einträge, deren aufgelöster Kunde == customerId und im Zeitraum liegt.
  const entries = timeEntries.filter(te => {
    if (!inRange(te.entry_date, range)) return false;
    const proj = te.project_id ? projectById[te.project_id] : null;
    const resolved = te.customer_id || proj?.customer_id || null;
    return resolved === customerId;
  });

  // Gruppieren nach Projekt; projektlose Einträge (Meetings/Support direkt am
  // Kunden) in einem eigenen Bucket.
  const byGroup = new Map(); // key → { id, title, entries: [] }
  const MEETING_KEY = '__meetings__';
  for (const te of entries) {
    let key, title;
    if (te.project_id && projectById[te.project_id]) {
      key = te.project_id;
      title = projectById[te.project_id].title || 'Projekt';
    } else {
      key = MEETING_KEY;
      title = 'Meetings & Support';
    }
    if (!byGroup.has(key)) byGroup.set(key, { id: key, title, entries: [] });
    byGroup.get(key).entries.push(te);
  }

  const groups = [...byGroup.values()].map(g => {
    const sorted = g.entries.slice().sort((a, b) =>
      (a.entry_date || '').localeCompare(b.entry_date || '')
      || (a.from_time || '').localeCompare(b.from_time || ''));
    const rows = sorted.map(te => ({
      date: te.entry_date,
      description: te.description || descFallback(te),
      min: durationMin(te.from_time, te.to_time),
    }));
    const totalMin = rows.reduce((s, r) => s + r.min, 0);
    return { id: g.id, title: g.title, entries: rows, totalMin };
  });

  // Meetings-Bucket ans Ende, sonst alphabetisch nach Projekttitel.
  groups.sort((a, b) => {
    if (a.id === MEETING_KEY) return 1;
    if (b.id === MEETING_KEY) return -1;
    return a.title.localeCompare(b.title, 'de');
  });

  const totalMin = groups.reduce((s, g) => s + g.totalMin, 0);
  const entryCount = groups.reduce((s, g) => s + g.entries.length, 0);

  return {
    customer,
    rangeLabel: labelForRange(range),
    groups,
    entryCount,
    totalMin,
  };
}

// Beschreibung fehlt → sinnvoller Platzhalter aus der Tätigkeitsart.
function descFallback(te) {
  if (te.kind === 'meeting') return 'Meeting';
  if (te.kind === 'support') return 'Support';
  return 'Projektarbeit';
}

function inRange(iso, range) {
  if (!iso) return false;
  if (!range) return true;
  if (range.from && iso < range.from) return false;
  if (range.to && iso > range.to) return false;
  return true;
}

function labelForRange(range) {
  if (!range || (!range.from && !range.to)) return 'Gesamter Zeitraum';
  return `${range.from ? deDate(range.from) : 'Anfang'} – ${range.to ? deDate(range.to) : 'heute'}`;
}

// --- HTML-Rendering (druckfertig) -------------------------------------------

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Erzeugt ein vollständiges, in sich geschlossenes HTML-Dokument (inline-CSS),
// das im Druckdialog sauber als A4-PDF aussieht.
export function renderReportHtml(report, opts = {}) {
  const { logoUrl = '', generatedLabel = '' } = opts;
  const customerName = report.customer?.name || 'Kunde';

  const groupsHtml = report.groups.map(g => {
    const rows = g.entries.map(e => `
      <tr>
        <td class="date">${esc(deDate(e.date))}</td>
        <td class="desc">${esc(e.description)}</td>
        <td class="dur">${esc(hoursColon(e.min))}</td>
      </tr>`).join('');
    return `
      <section class="group">
        <h2>${esc(g.title)}</h2>
        <table>
          <thead>
            <tr><th class="date">Datum</th><th class="desc">Leistung</th><th class="dur">Dauer</th></tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr>
              <td></td>
              <td class="sub-label">Summe ${esc(g.title)}</td>
              <td class="dur sub">${esc(hoursColon(g.totalMin))}</td>
            </tr>
          </tfoot>
        </table>
      </section>`;
  }).join('');

  const empty = report.groups.length === 0
    ? '<p class="empty">Für den gewählten Zeitraum liegen keine erfassten Zeiten vor.</p>'
    : '';

  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8">
<title>Zeitnachweis ${esc(customerName)} – ${esc(report.rangeLabel)}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
    color: #1a1a1a; font-size: 12px; line-height: 1.45;
    padding: 32px 36px;
  }
  header {
    display: flex; align-items: flex-start; justify-content: space-between;
    border-bottom: 2px solid #1a1a1a; padding-bottom: 16px; margin-bottom: 24px;
  }
  header .logo { height: 30px; }
  header .brand-fallback { font-size: 18px; font-weight: 700; letter-spacing: .02em; }
  header .meta { text-align: right; font-size: 11px; color: #555; }
  h1 { font-size: 20px; margin: 0 0 4px; font-weight: 600; }
  .subtitle { color: #555; font-size: 12px; margin-bottom: 28px; }
  .subtitle b { color: #1a1a1a; }
  section.group { margin-bottom: 22px; page-break-inside: avoid; }
  h2 {
    font-size: 13px; font-weight: 600; margin: 0 0 6px;
    padding-bottom: 4px; border-bottom: 1px solid #ddd;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { text-align: left; padding: 5px 8px; vertical-align: top; }
  thead th {
    font-size: 10px; text-transform: uppercase; letter-spacing: .04em;
    color: #888; font-weight: 600; border-bottom: 1px solid #eee;
  }
  tbody td { border-bottom: 1px solid #f2f2f2; }
  td.date, th.date { width: 90px; white-space: nowrap; }
  td.dur, th.dur { width: 80px; text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  tfoot td { padding-top: 8px; font-weight: 600; }
  tfoot .sub-label { text-align: right; color: #555; font-weight: 500; }
  tfoot .dur.sub { border-top: 1px solid #ccc; }
  .grand {
    margin-top: 10px; display: flex; justify-content: space-between; align-items: baseline;
    border-top: 2px solid #1a1a1a; padding-top: 12px;
  }
  .grand .label { font-size: 14px; font-weight: 600; }
  .grand .value { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .empty { color: #888; font-style: italic; padding: 20px 0; }
  footer { margin-top: 40px; padding-top: 12px; border-top: 1px solid #eee; font-size: 10px; color: #999; }
  @media print {
    body { padding: 0; }
    @page { margin: 18mm 16mm; }
  }
</style></head>
<body>
  <header>
    ${logoUrl
      ? `<img class="logo" src="${esc(logoUrl)}" alt="Markenegger">`
      : `<div class="brand-fallback">Markenegger</div>`}
    <div class="meta">
      Zeit- / Leistungsnachweis${generatedLabel ? `<br>Erstellt am ${esc(generatedLabel)}` : ''}
    </div>
  </header>

  <h1>${esc(customerName)}</h1>
  <div class="subtitle">Zeitraum: <b>${esc(report.rangeLabel)}</b> &nbsp;·&nbsp; ${report.entryCount} Einträge</div>

  ${groupsHtml}
  ${empty}

  ${report.groups.length > 0 ? `
  <div class="grand">
    <div class="label">Gesamt</div>
    <div class="value">${esc(hoursColon(report.totalMin))}</div>
  </div>` : ''}

  <footer>
    Automatisch erstellt aus pm.markenegger.com · Alle Angaben ohne Gewähr.
  </footer>
</body></html>`;
}

// Rendert den Report in einem versteckten iframe und startet den Druckdialog
// („Als PDF sichern"). Bewusst KEIN window.open — das würde von Popup-Blockern
// abgefangen (und liefert mit noopener sogar null zurück). Der iframe-Druck ist
// gegen Popup-Blocker immun. Gibt true zurück, sobald der Druck angestoßen ist.
export function printReportHtml(html) {
  if (typeof document === 'undefined') return false;

  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, {
    position: 'fixed', right: '0', bottom: '0',
    width: '0', height: '0', border: '0', visibility: 'hidden',
  });
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);

  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    const win = iframe.contentWindow;
    try { win.focus(); win.print(); } catch { /* noop */ }
    // iframe erst nach dem Druckdialog entfernen.
    setTimeout(() => { try { iframe.remove(); } catch { /* noop */ } }, 1000);
  };

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  // Drucken, sobald Inhalt + Logo geladen sind; Fallback, falls onload ausbleibt.
  iframe.contentWindow.onload = () => setTimeout(doPrint, 200);
  setTimeout(doPrint, 800);
  return true;
}

// --- CSV (Bonus, gleicher Datensatz, ohne Sätze) ----------------------------

function csvCell(v) {
  const s = v == null ? '' : String(v);
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function buildCustomerCsv(report) {
  const lines = [['Projekt', 'Datum', 'Leistung', 'Dauer (Std, dezimal)'].map(csvCell).join(';')];
  for (const g of report.groups) {
    for (const e of g.entries) {
      lines.push([g.title, deDate(e.date), e.description, hoursDecimal(e.min)].map(csvCell).join(';'));
    }
    lines.push([g.title, '', 'Summe', hoursDecimal(g.totalMin)].map(csvCell).join(';'));
  }
  lines.push(['', '', 'GESAMT', hoursDecimal(report.totalMin)].map(csvCell).join(';'));
  return lines.join('\r\n');
}

export function downloadCsv(filename, text) {
  if (typeof document === 'undefined') return;
  const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Dateiname-sicherer Kundenname.
export function safeName(s) {
  return String(s || 'kunde').normalize('NFKD').replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'kunde';
}
